// ============================================================================
// OBT — Kademeli devir: DAĞITIM görevleri ekranı (BM/MY)
// ============================================================================
// Aktörün DELEGATE (dağıtım görevlisi) olduğu bilgi taleplerini listeler ve
// "Formen'lerime Dağıt" eylemini sunar. Dağıtım: alt-zincirdeki Formen'ler
// respondent hedef olarak yayına eklenir (backend idempotent). Kendisi cevaplamaz.
// ============================================================================

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Send, Loader2, CheckCircle2, RefreshCw, Users } from 'lucide-react';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
} from '../ObtStates';
import { useMyPublications } from './useMyPublications';
import { distributePublication } from './responseApi';
import { normalizeResponseError } from './responseErrors';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DistributeCard({ pub, onReload }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const due = formatDate(pub.response_due_at);

  const handleDistribute = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await distributePublication(pub.publication_id);
      setResult(res);
      if (onReload) onReload();
    } catch (e) {
      setError(normalizeResponseError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm font-semibold text-gray-900">
            {pub.campaign_title || 'Başlıksız bilgi talebi'}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>
              Konu:{' '}
              <span className="text-gray-700">{pub.topic || 'belirtilmemiş'}</span>
            </span>
            {pub.published_by?.name && (
              <span>
                Gönderen: <span className="text-gray-700">{pub.published_by.name}</span>
              </span>
            )}
            {due && <span>Son cevap: {due}</span>}
          </div>
          <p className="text-xs text-blue-700">
            Bu talep size <strong>dağıtım için</strong> geldi. Cevabı Formen'ler verir.
          </p>
          {result && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-green-700">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {result.added > 0
                ? `${result.added} Formen'e dağıtıldı${
                    result.added_parks ? ` (${result.added_parks} park eklendi)` : ''
                  }.`
                : 'Zaten dağıtılmış (yeni Formen eklenmedi).'}
            </p>
          )}
          {error && (
            <p className="text-xs text-red-600" role="alert">
              {error.message || 'Dağıtım yapılamadı.'}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center">
          <Button onClick={handleDistribute} disabled={submitting}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Formen'lerime Dağıt
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {() => void} [props.onBack]
 */
export default function DistributeScreen({ enabled = true, onBack }) {
  const { loading, error, items, reload } = useMyPublications({ enabled });
  const delegatePubs = useMemo(
    () => items.filter((p) => p.is_delegate),
    [items]
  );

  return (
    <div className="space-y-4">
      <ObtPageHeader
        onBack={onBack}
        title="Dağıtım Görevleri"
        description="Size dağıtım için gelen bilgi taleplerini Formen'lerinize dağıtın. Cevabı park sahibi Formen verir."
        actions={
          <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Yenile
          </Button>
        }
      />

      {loading && <ObtLoading label="Dağıtım görevleri yükleniyor…" />}
      {!loading && error && <ObtErrorState error={error} onRetry={reload} />}
      {!loading && !error && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" aria-hidden="true" />
              Dağıtılacak Bilgi Talepleri
            </CardTitle>
          </CardHeader>
          <CardContent>
            {delegatePubs.length === 0 ? (
              <ObtEmptyState
                title="Dağıtım göreviniz yok"
                description="Üst makam size dağıtım için bir bilgi talebi gönderdiğinde burada görünür ve Formen'lerinize dağıtabilirsiniz."
              />
            ) : (
              <ul className="space-y-3">
                {delegatePubs.map((pub) => (
                  <DistributeCard
                    key={pub.publication_id}
                    pub={pub}
                    onReload={reload}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
